/**
 * Saved Projects on the Enlist kit: what a project needs from the
 * theme after the kit has drawn it (sections/enlist-lists.liquid).
 *
 * 1. A working card slider. Blaze measures .blaze-track's children as slides,
 *    and the kit fills and refills the track long after page load, so each
 *    slider is rebuilt when its slide COUNT changes (not on every mutation,
 *    which would reset a row mid-swipe and could feed back on itself).
 * 2. The inc-VAT total. The kit writes the ex-VAT total; Liquid never sees the
 *    number, so the second line is worked out here, at the rate
 *    snippets/css-variables publishes on <html> (data-vat-rate).
 *
 * 3. The bin. It opens the kit's rename dialog already at "delete this
 *    project?", so deleting stays one confirm away and uses the kit's code.
 *
 * Ported from assets/qfl-outfit-panel.js, which did the same for Enlist's own
 * outfits renderer; only the selectors changed.
 */

const ROOT = "[data-enlist-lists]";
const SLIDER = "[data-ukfd-slider]";
const slideCounts = new WeakMap();

const getController = async () => {
  if (window.themeBlazeSliderController) return window.themeBlazeSliderController;
  const { blazeSliderController } = await import("@theme/blaze-slider-controller");
  return blazeSliderController;
};

const rebuildSliders = async (root) => {
  const stale = [...root.querySelectorAll(SLIDER)].filter((slider) => {
    const count = slider.querySelectorAll(".blaze-track > li").length;
    if (!count || slideCounts.get(slider) === count) return false;
    slideCounts.set(slider, count);
    return true;
  });
  if (!stale.length) return;
  const controller = await getController();
  if (!controller) return;
  stale.forEach((slider) => {
    // destroy() first: a refresh would carry over measurements taken against
    // the cards the kit has just replaced.
    controller.destroy(slider);
    controller.initSlider(slider);
  });
};

const money = (value) =>
  new Intl.NumberFormat(document.documentElement.lang || "en-GB", {
    style: "currency",
    currency: window.Shopify?.currency?.active || "GBP",
  }).format(value);

// Falls back to 20, not to no VAT: a missing setting must not quietly turn the
// inc-VAT figure into the ex-VAT one.
const vatMultiplier = () => {
  const rate = parseFloat(document.documentElement.dataset.vatRate);
  return 1 + (Number.isFinite(rate) ? rate : 20) / 100;
};

const parseMoney = (text) => {
  const value = Number.parseFloat((text || "").replace(/[^\d.,]/g, "").replace(/,/g, ""));
  return Number.isFinite(value) ? value : null;
};

const fillIncVat = (root) => {
  root.querySelectorAll("[data-ukfd-totals]").forEach((totals) => {
    const line = totals.querySelector("[data-ukfd-total-inc]");
    if (!line) return;
    const ex = parseMoney(totals.querySelector("[data-ukfd-total-ex]")?.textContent);
    if (ex === null || ex === 0) {
      line.hidden = true;
      return;
    }
    const inc =
      `${money(Math.round(ex * vatMultiplier() * 100) / 100)} ${line.dataset.vatLabel || ""}`.trim();
    if (line.textContent !== inc) line.textContent = inc;
    line.hidden = false;
  });
};

// The bin: the kit's rename dialog, opened straight at its delete confirm.
// The dialog then goes into delete mode (enlist-ukfd.css hides the rename
// row), so only one Delete and one Cancel show, and that Cancel closes the
// dialog rather than dropping back to a rename nobody asked for. "Delete
// project" from the pencil's rename switches to the same mode. Cleared on close,
// so the pencil always opens a plain rename.
const DIALOG = "[data-enlist-edit-dialog]";
const deleteMode = (dialog) => {
  dialog.dataset.ukfdMode = "delete";
  const confirm = dialog.querySelector("[data-enlist-edit-confirm]");
  if (confirm) confirm.hidden = false;
};
const onDelete = (event) => {
  const target = event.target instanceof Element ? event.target : null;
  if (!target) return;
  const dialog = target.closest(DIALOG);
  if (dialog) {
    if (target.closest("[data-enlist-edit-delete]")) deleteMode(dialog);
    else if (
      target.closest("[data-enlist-edit-confirm-no]") &&
      dialog.dataset.ukfdMode === "delete"
    )
      dialog.close();
    return;
  }
  const bin = target.closest("[data-ukfd-delete]");
  if (!bin) return;
  event.preventDefault();
  const edit = bin.closest("[data-enlist-list-row]")?.querySelector("[data-enlist-list-edit]");
  if (!edit) return;
  edit.click();
  const opened = document.querySelector(DIALOG);
  if (opened) deleteMode(opened);
};
const onDialogClose = (event) => {
  if (event.target instanceof Element && event.target.matches(DIALOG))
    delete event.target.dataset.ukfdMode;
};

// 4. Quantities. The kit saves a project card's quantity on `change` and
//    fills it from the project with `input`; assets/product-quantity.js sets
//    the hidden input without firing either, and shows the number in a span.
//    So: tell the kit after a +/- (this listener is on document, the theme's
//    on body, so the value is already set), and mirror a filled value into
//    the span, the card's Add to Cart and its price.
const QTY_ROW = "[data-enlist-list-row] li.enlist-item";
const onStep = (event) => {
  const step =
    event.target instanceof Element &&
    event.target.closest(
      "[quantity-select] [data-quantity-increment], [quantity-select] [data-quantity-decrement]",
    );
  if (!step || !step.closest(QTY_ROW)) return;
  step
    .closest("[quantity-select]")
    .querySelector('input[name="quantity"]')
    ?.dispatchEvent(new Event("change", { bubbles: true }));
};
const onFill = (event) => {
  const input = event.target instanceof Element && event.target.closest('input[name="quantity"]');
  if (!input || !input.closest(QTY_ROW)) return;
  const qty = input.value;
  const display = input
    .closest("[quantity-select]")
    ?.querySelector("[data-quantity-value],[quantity]");
  if (display && display.textContent !== qty) display.textContent = qty;
  const form = input.closest("product-form");
  const addToCart = form?.querySelector("[add-to-cart]");
  if (addToCart) addToCart.dataset.quantity = qty;
  const price = form?.querySelector("[data-variant-price-submit]");
  if (price && window.Shopify?.formatMoney) {
    price.innerHTML = `- ${window.Shopify.formatMoney(parseFloat(price.dataset.price || "0") * Number(qty), form.dataset.moneyFormat || "")}`;
  }
};

const init = () => {
  const root = document.querySelector(ROOT);
  if (!root || root.dataset.ukfdBound === "true") return;
  root.dataset.ukfdBound = "true";
  const sync = () => {
    fillIncVat(root);
    rebuildSliders(root);
  };
  sync();
  new MutationObserver(sync).observe(root, { childList: true, subtree: true, characterData: true });
  document.addEventListener("qfl:wishlist:changed", sync);
  root.addEventListener("click", onDelete);
  root.addEventListener("close", onDialogClose, true); // close does not bubble
  document.addEventListener("click", onStep);
  root.addEventListener("input", onFill);
};

if (document.readyState === "loading") document.addEventListener("DOMContentLoaded", init);
else init();
document.addEventListener("shopify:section:load", init); // the theme editor re-renders the section
